// components/MultiBrandBarChart.jsx
import { Card, Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  Cell
} from "recharts";

export default function DoubleBarChart({
  title,
  xLabels,
  series,
  dataMatrix,
  yTicks
}) {
  // ===== BUILD CHART DATA =====
  const chartData = xLabels.map((brand, i) => ({
    brand,
    last: dataMatrix.actual[i][0],
    current: dataMatrix.actual[i][1]
  }));

  /**
   * ===== DYNAMIC COLOR RESOLVER =====
   * Rules:
   * 1. If series label contains brand → brand-specific color
   * 2. Else → default color for that key
   */
  const getBarColor = (brand, key) => {
    // Brand-specific
    const brandSpecific = series.find(
      s =>
        s.key === key &&
        s.label.toLowerCase().includes(brand.toLowerCase())
    );

    if (brandSpecific) return brandSpecific.color;

    // Default series (label does NOT include any brand)
    const defaultSeries = series.find(
      s =>
        s.key === key &&
        !xLabels.some(b =>
          s.label.toLowerCase().includes(b.toLowerCase())
        )
    );

    return defaultSeries?.color || "#999";
  };
  const chartWidth = Math.max(chartData.length * 120, 900);
  return (
    <Card
      sx={{
        width: "100%",
        borderRadius: "12px !important",
        background: "#fff",
        boxShadow: "0px 1px 12px rgba(0, 0, 0, 0.1)",
        p: 3,
        pt: 6,
        position: "relative"
      }}
    >
      {/* ===== HEADER BAR ===== */}
      <Box
        sx={{
          position: "absolute",
          top: "-10px",
          left: "12px",
          right: "12px",
          height: "36px",
          background: "#115f9c",
          borderRadius: "4px !important",
          display: "flex",
          alignItems: "center",
          px: "12px",
          color: "#fff",
          fontSize: { xs: 12, sm: 12, md: 16 },
          fontWeight: 200,
          boxShadow: "0px 1px 16px rgba(128, 203, 255, 0.5)"
        }}
      >
        {title}
      </Box>

      {/* ===== CHART ===== */}
      <Box sx={{
        width: "100%",
        height: 420,
        overflowX: "auto",
        overflowY: "hidden",
      }}>
        <Box
          sx={{
            width: {
              xs: chartWidth,   //  enable horizontal scroll on mobile
              sm: chartWidth,   // enable horizontal scroll on tablet
              md: chartWidth,
              lg: "100%"
            },
            minWidth: {
              xs: chartWidth,
              sm: chartWidth,
              md: chartWidth,
              lg: "100%"
            },
            height: "100%",
          }}
        >
          <ResponsiveContainer>
            <BarChart
              data={chartData}
              barCategoryGap={42}
              barGap={10}
              margin={{ top: 30, right: 40, left: 30, bottom: 10 }}
            >
              <CartesianGrid stroke="#1111110D" vertical={false} />

              <YAxis
                domain={[0, 5]}
                ticks={yTicks}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: "#11111180", dx: -12, fontWeight: 200 }} // move labels left
                label={{
                  value: "Reported Dollars Sold ($B)",
                  angle: -90,
                  position: "insideLeft",
                  dy: 60,
                  style: { fontSize: 10, fill: "#111111", fontWeight: 200 }
                }}
              />

              <XAxis
                dataKey="brand"
                interval={0}
                tick={{ fontSize: 10, color: "#111111", fontWeight: 200, }}
                tickLine={false}
                axisLine={{ stroke: "#DADADA" }}

              />

              <Tooltip
                formatter={(value, name) => {
                  if (name === "last") {
                    return [`$${value}B`, "Dollars Sold – Last Period"];
                  }
                  if (name === "current") {
                    return [`$${value}B`, "Dollars Sold – This Period"];
                  }
                  return [`$${value}B`, name];
                }}
                labelFormatter={(label) => `Brand: ${label}`}
              />

              {/* ===== LAST PERIOD ===== */}
              <Bar dataKey="last" barSize={48}>
                {chartData.map((row, i) => (
                  <Cell
                    key={i}
                    fill={getBarColor(row.brand, "last")}
                  />
                ))}

                <LabelList
                  dataKey="last"
                  position="top"
                  formatter={(v) => `$${v}B`}
                  style={{ fontSize: 8, fill: "#11111180" }}
                />
              </Bar>

              {/* ===== THIS PERIOD ===== */}
              <Bar dataKey="current" barSize={48}>
                {chartData.map((row, i) => (
                  <Cell
                    key={i}
                    fill={getBarColor(row.brand, "current")}
                  />
                ))}

                <LabelList
                  dataKey="current"
                  position="top"
                  formatter={(v) => `$${v}B`}
                  style={{ fontSize: 8, fill: "#11111180" }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>

        </Box>

      </Box>

      {/* ===== CUSTOM LEGEND ===== */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: 2,
          mt: '16px'
        }}
      >
        {series.map((s) => (
          <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: s.color
              }}
            />
            <Typography fontSize={10} style={{ color: "#111111", fontWeight: 200 }}>{s.label}</Typography>
          </Box>
        ))}
      </Box>
    </Card>
  );
}
